import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { InstallButton } from "@/components/Pwa";
import { PushConsent } from "@/components/PushConsent";
import { ClaimBooking } from "@/components/ClaimBooking";
import { useSettings } from "@/components/SocialLinks";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, Users, MapPin, KeyRound, Wifi, Car, Clock, Navigation, Phone, Mail, Star, ShoppingBasket, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

function PracticalCard({ p, home }) {
  const rows = [
    [MapPin, "Adresse", p.address],
    [KeyRound, "Accès", p.access_code],
    [Wifi, "Wifi", p.wifi_name ? `${p.wifi_name}${p.wifi_password ? ` · mot de passe : ${p.wifi_password}` : ""}` : ""],
    [Car, "Parking", p.parking],
    [Clock, "Horaires", `Arrivée à partir de ${p.checkin_time || "16:00"} · Départ avant ${p.checkout_time || "10:00"}`],
  ].filter((r) => r[2]);
  const maps = p.address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(p.address)}` : null;
  return (
    <div className="mt-3 p-4 rounded-xl bg-secondary border border-olive/20" data-testid="practical-info">
      <p className="text-xs uppercase tracking-wider text-olive font-semibold mb-2">Mon séjour — infos pratiques</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {rows.map(([Icon, label, val]) => (
          <p key={label} className="flex items-start gap-2"><Icon className="w-4 h-4 text-rose-brand mt-0.5 shrink-0" /><span><span className="text-muted-foreground">{label} : </span>{val}</span></p>
        ))}
      </div>
      {p.notes && <div className="mt-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Règlement & consignes</p><p className="text-sm mt-1 whitespace-pre-line text-foreground">{p.notes}</p></div>}
      <div className="flex flex-wrap gap-2 mt-4">
        {maps && <a href={maps} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-olive text-white" data-testid="route-button"><Navigation className="w-3.5 h-3.5" /> Itinéraire</a>}
        <Link to="/courses" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-border text-olive"><ShoppingBasket className="w-3.5 h-3.5" /> Drive courses</Link>
        <Link to="/messagerie" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-border text-olive"><MessageCircle className="w-3.5 h-3.5" /> Contacter L’Orne Park</Link>
        {home?.phone && <a href={`tel:${home.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-border text-olive"><Phone className="w-3.5 h-3.5" /> {home.phone}</a>}
        {home?.contact_email && <a href={`mailto:${home.contact_email}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-border text-olive"><Mail className="w-3.5 h-3.5" /> Email</a>}
        {home?.google_reviews_url && <a href={home.google_reviews_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-brand text-white" data-testid="google-review-button"><Star className="w-3.5 h-3.5" /> Laisser un avis Google</a>}
      </div>
    </div>
  );
}

const STATUS = {
  confirmed: { label: "Confirmé", cls: "bg-olive text-white" },
  pending: { label: "En attente de paiement", cls: "bg-rosebrand-pale text-rose-brand" },
  cancelled: { label: "Annulé", cls: "bg-secondary text-muted-foreground" },
  refunded: { label: "Annulé · Remboursé", cls: "bg-secondary text-olive" },
};

export default function TravelerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const settings = useSettings();

  const load = useCallback(() => api.get("/bookings").then(({ data }) => setBookings(data)), []);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.role) { navigate("/connexion"); return; }
    load();
  }, [user, loading, navigate, load]);

  const cancel = async (id) => {
    if (!window.confirm("Annuler cette réservation ?")) return;
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`);
      const parts = ["Réservation annulée"];
      if (data.refund === "refunded") parts.push("remboursement Stripe effectué");
      if (data.refund === "error") parts.push("remboursement à faire manuellement");
      if (data.lodgify === "declined") parts.push("Lodgify mis à jour");
      toast.success(parts.join(" · "));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-serif text-4xl text-olive">Mes séjours</h1>
        <p className="text-muted-foreground mt-1 mb-6">Vos réservations et leur statut</p>
        <div className="mb-8 space-y-3">
          <InstallButton />
          <PushConsent />
          <ClaimBooking onClaimed={load} />
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-20 rounded-2xl border border-dashed border-border" data-testid="no-bookings">
            <p className="text-muted-foreground">Aucune réservation pour l'instant.</p>
            <button onClick={() => navigate("/")} className="text-rose-brand font-semibold mt-2">Explorer les logements</button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => {
              const st = (b.status === "cancelled" && b.payment_status === "refunded") ? STATUS.refunded : (STATUS[b.status] || STATUS.pending);
              return (
                <div key={b.booking_id} className="flex flex-col sm:flex-row gap-4 p-4 rounded-2xl bg-white border border-border hover-lift" data-testid={`booking-${b.booking_id}`}>
                  <img src={b.listing_image} alt="" className="w-full sm:w-40 h-28 object-cover rounded-xl" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-serif text-xl text-olive">{b.listing_title}</h3>
                      <Badge className={st.cls}>{st.label}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {b.check_in} → {b.check_out}</span>
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {b.guests} voyageur(s)</span>
                    </div>
                    <p className="mt-2 font-bold text-olive">{b.total} € <span className="font-normal text-muted-foreground text-sm">· {b.nights} nuit(s)</span></p>
                    {b.practical && <PracticalCard p={b.practical} home={settings?.home} />}
                    {b.status !== "cancelled" && b.check_in >= new Date().toISOString().slice(0, 10) && (
                      <button onClick={() => cancel(b.booking_id)} className="mt-2 text-xs text-muted-foreground hover:text-destructive underline" data-testid={`cancel-booking-${b.booking_id}`}>Annuler la réservation</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
